import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { Button } from './Button';

/** Accessible modal dialog: ESC and click-outside close it; opt-in focus trap and focus return (`trapFocus`, `returnFocusTo`). */
/** Panel widths. `lg` is the long-standing default; wider sizes are opt-in per dialog. */
const SIZES = {
  lg: 'max-w-lg',
  '3xl': 'max-w-3xl',
  '5xl': 'max-w-5xl',
} as const;
export type ModalSize = keyof typeof SIZES;

/**
 * The controls Tab can land on, for `trapFocus`. The trap then drops anything under `aria-hidden="true"`,
 * `hidden` or `inert`. There are no layout checks (jsdom has no layout), so a control hidden only by CSS
 * still counts; hide it from assistive tech too and it is skipped here as well. A scroll container the
 * browser makes a Tab stop is not listed: the trap places it by its position among the controls.
 */
const TABBABLE =
  'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/** The controls Tab can land on inside `root`, in document order (see TABBABLE). */
function tabbableIn(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(TABBABLE)).filter(
    (el) => el.tabIndex >= 0 && !el.closest('[aria-hidden="true"],[hidden],[inert]'),
  );
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'lg',
  bodyClassName,
  closeDisabled = false,
  closeDisabledReason,
  trapFocus = false,
  returnFocusTo,
  closeButtonClassName,
  aboveFooter,
  belowFooter,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Panel width. Additive — every existing caller keeps `lg`. */
  size?: ModalSize;
  /** Replaces the default scrolling body padding, for dialogs that own their own layout. */
  bodyClassName?: string;
  /**
   * Refuse to close. Used while a generation is in flight: the work is already paid for and the
   * result would be abandoned. ESC and the backdrop are inert too, not just the ✕ — a disabled
   * button the user can route around is not a guard.
   */
  closeDisabled?: boolean;
  /** Why closing is blocked, surfaced as the ✕ button's tooltip. */
  closeDisabledReason?: string;
  /**
   * Keep Tab inside the dialog: Tab past the last control goes to the first, Shift+Tab before the first
   * goes to the last. Off by default. The controls are read from the `[role="dialog"]` root on every Tab,
   * so a Select's list (portalled there, beside the panel) stays in the cycle; the backdrop leaves it. A
   * Tab pressed while focus has fallen to <body> (the control holding it turned disabled) enters it too.
   * Browsers also stop on things that are not controls: Chrome and Edge make a scrolling body with no
   * control in it a Tab stop, and Firefox stops on scroll containers. A Tab from such a stop inside the
   * dialog is left to the browser while a control lies beyond it in that direction, so the cycle still
   * reaches every control both ways.
   */
  trapFocus?: boolean;
  /**
   * Focused when the dialog closes or unmounts: usually the control that opened it. Skipped if by then it
   * has left the document or is disabled. Off by default.
   */
  returnFocusTo?: HTMLElement | null;
  /** Extra classes for the ✕ button (e.g. a 44px touch target); its `aria-label` stays "Close". */
  closeButtonClassName?: string;
  /** Rendered between the body and the footer, outside the scrolling body, so it never scrolls away. */
  aboveFooter?: ReactNode;
  /** Rendered after the footer (e.g. a live region for an error under the buttons). */
  belowFooter?: ReactNode;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef(returnFocusTo);

  // ESC-to-close + scroll lock. Depends on `onClose` (may change identity on a parent re-render),
  // so this can re-run while the modal is open — kept side-effect-light on purpose.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Capture phase + stopPropagation so only the INNERMOST open dialog closes. Without this a
      // modal opened on top of another (the composer's Media Library picker) closed both, throwing
      // away the post the outer one was editing.
      e.stopPropagation();
      if (closeDisabled) return;
      onClose();
    };
    document.addEventListener('keydown', onKey, true);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, closeDisabled]);

  // Focus recovery for the trap (opt-in). The trap below runs on the dialog's own keys only, so it never
  // sees a Tab pressed while focus is on <body>. That is where focus lands when the control holding it
  // turns disabled (Continue while it loads, or once a refetched key status refuses it): browsers that
  // apply the HTML focus-fixup rule, Firefox among them, drop it there, and the next Tab would walk out
  // into the page behind the dialog. So while focus is on <body> (or nowhere), Tab enters the dialog at
  // its first control and Shift+Tab at its last. Focus anywhere else is left alone: outside this dialog
  // it belongs to a layer above it.
  useEffect(() => {
    if (!open || !trapFocus) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const active = document.activeElement;
      if (active !== null && active !== document.body) return;
      const root = rootRef.current;
      if (!root) return;
      e.preventDefault();
      const items = tabbableIn(root);
      if (items.length === 0) {
        panelRef.current?.focus();
        return;
      }
      items[e.shiftKey ? items.length - 1 : 0].focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, trapFocus]);

  // Move focus into the dialog ONCE per open (keyed on `open` only, so it never re-steals focus
  // from what the user is typing when the parent re-renders). Skip if a child already holds focus
  // (e.g. an autoFocus textarea) so that child keeps it.
  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (panel && !panel.contains(document.activeElement)) panel.focus();
  }, [open]);

  // `returnFocusTo` as of the last render, read by the close below. A ref, so a new element never re-runs
  // that effect (its cleanup IS the close).
  useEffect(() => {
    returnFocusRef.current = returnFocusTo;
  }, [returnFocusTo]);

  // Focus return (opt-in). The cleanup runs when `open` turns false and when the dialog unmounts while
  // open. Under StrictMode, development's mount -> cleanup -> mount focuses the opener here and then the
  // panel again (the effect above re-runs), so focus still ends inside the dialog.
  useEffect(() => {
    if (!open) return;
    return () => {
      const target = returnFocusRef.current;
      if (target?.isConnected && !target.matches(':disabled')) target.focus();
    };
  }, [open]);

  // The focus trap (opt-in). Only a Tab that would leave the dialog is taken over: from the last control,
  // Shift+Tab from the first, and either from the panel (where focus starts) or from outside the dialog's
  // DOM (a layer portalled out of it, whose keys still reach it through React). A stop inside the dialog
  // that the list leaves out (a scrolling body the browser made focusable) is placed by its position: a
  // Tab from it is taken over only when no control lies beyond it that way. Anything else, a Tab between
  // two controls included, is left to the browser.
  const keepFocusInside = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const root = rootRef.current;
    if (!root) return;
    const items = tabbableIn(root);
    if (items.length === 0) {
      e.preventDefault();
      panelRef.current?.focus();
      return;
    }
    const active = document.activeElement as HTMLElement | null;
    const at = items.indexOf(active as HTMLElement);
    const beyond = e.shiftKey ? Node.DOCUMENT_POSITION_PRECEDING : Node.DOCUMENT_POSITION_FOLLOWING;
    if (
      at === -1 &&
      active !== null &&
      active !== panelRef.current &&
      root.contains(active) &&
      items.some((el) => active.compareDocumentPosition(el) & beyond)
    ) {
      return;
    }
    const leaving = e.shiftKey ? at <= 0 : at === -1 || at === items.length - 1;
    if (!leaving) return;
    e.preventDefault();
    items[e.shiftKey ? items.length - 1 : 0].focus();
  };

  if (!open) return null;

  return (
    <div
      ref={rootRef}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onKeyDown={trapFocus ? keepFocusInside : undefined}
    >
      <button
        type="button"
        aria-label="Close dialog"
        tabIndex={trapFocus ? -1 : undefined}
        className="absolute inset-0 cursor-default bg-black/40"
        onClick={() => {
          if (!closeDisabled) onClose();
        }}
      />
      <div
        ref={panelRef}
        tabIndex={-1}
        className={`relative z-10 flex max-h-[92vh] w-full flex-col overflow-hidden rounded-xl bg-card shadow-panel focus:outline-none ${SIZES[size]}`}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-line-soft px-5 py-4">
          <h2 className="text-base font-semibold text-ink">{title}</h2>
          <Button
            variant="ghost"
            size="sm"
            className={closeButtonClassName}
            onClick={onClose}
            disabled={closeDisabled}
            title={closeDisabled ? closeDisabledReason : undefined}
            aria-label="Close"
          >
            ✕
          </Button>
        </div>
        <div className={bodyClassName ?? 'max-h-[70vh] overflow-y-auto px-5 py-4'}>{children}</div>
        {aboveFooter ? <div className="shrink-0 px-5 pb-4">{aboveFooter}</div> : null}
        {footer && (
          <div className="flex shrink-0 justify-end gap-2 border-t border-line-soft px-5 py-4">{footer}</div>
        )}
        {belowFooter ? <div className="shrink-0">{belowFooter}</div> : null}
      </div>
    </div>
  );
}
